import {
  FuelReading,
  FuelSlot,
  FuelSummary,
  FuelSummaryDay,
} from '../services/fuelReadingsService';

export const SLOT_LABEL: Record<FuelSlot, string> = {
  abertura: 'Abertura',
  fechamento: 'Fechamento',
};

// "07:00,17:00" -> ["07:00", "17:00"]; "7:00" vira "07:00" (igual à api)
function parseTimes(value?: string | null): string[] {
  return (value || '')
    .split(',')
    .map(item => item.trim())
    .filter(item => /^([01]?\d|2[0-3]):[0-5]\d$/.test(item))
    .map(item => item.padStart(5, '0'));
}

// horários do dia: no sábado o posto fecha mais cedo (11h)
export function timesFor(
  pump: { expected_times: string; saturday_times?: string | null },
  date: Date,
): string[] {
  const saturday = date.getDay() === 6 ? parseTimes(pump.saturday_times) : [];
  const times = saturday.length ? saturday : parseTimes(pump.expected_times);

  return [times[0] || '07:00', times[1] || '17:00'];
}

const minutesOf = (time: string) => {
  const [hours, minutes] = time.split(':').map(Number);

  return hours * 60 + minutes;
};

// até o meio do expediente é abertura; depois, fechamento
export function slotFor(
  date: Date,
  times: string[] = ['07:00', '17:00'],
): FuelSlot {
  const middle = (minutesOf(times[0]) + minutesOf(times[1])) / 2;

  return date.getHours() * 60 + date.getMinutes() < middle
    ? 'abertura'
    : 'fechamento';
}

// "07:00" -> "7h", "11:30" -> "11h30"
export function hourLabel(time: string): string {
  const [hours, minutes] = time.split(':');

  return `${Number(hours)}h${minutes === '00' ? '' : minutes}`;
}

// aferição (PRO PA nº 003): medida padrão de 20 l, régua de -60 a +100 ml
export const CALIBRATION_VOLUME = 20;
export const CALIBRATION_MAX_OVER_ML = 100;
export const CALIBRATION_MAX_UNDER_ML = -60;

export function calibrationApproved(deviationMl: number): boolean {
  return (
    deviationMl >= CALIBRATION_MAX_UNDER_ML &&
    deviationMl <= CALIBRATION_MAX_OVER_ML
  );
}

// litros da descarga: "10.000" é dez mil (ponto de milhar)
export function parseLiters(value: string): number {
  const text = value.trim();
  const normalized = /^\d{1,3}(\.\d{3})+(,\d+)?$/.test(text)
    ? text.replace(/\./g, '').replace(',', '.')
    : text.replace(',', '.');

  return /^\d+(\.\d{1,3})?$/.test(normalized) ? Number(normalized) : NaN;
}

// nº da nf só com dígitos e sem zeros à esquerda
export function normalizeDocument(value: string): string {
  return value.replace(/\D/g, '').replace(/^0+/, '');
}

// número digitado no celular: aceita "123456", "123456,7" e "123.456,789"
export function parseReading(value: string): number {
  const text = value.trim();
  const normalized = text.includes(',')
    ? text.replace(/\./g, '').replace(',', '.')
    : text;

  return /^\d+(\.\d{1,3})?$/.test(normalized) ? Number(normalized) : NaN;
}

export function formatLiters(
  value: number | null | undefined,
  digits = 0,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }

  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: Math.max(digits, 3),
  });
}

export function formatReading(value: number | null | undefined): string {
  return formatLiters(value, 0);
}

// régua da aferição: "+40 ml", "-30 ml", "0 ml"
export function formatDeviation(value: number): string {
  return `${value > 0 ? '+' : ''}${formatLiters(value)} ml`;
}

// "2026-10-20T07:05:00" -> "20/10 07:05"
export function formatDateTime(value: string | null, withYear = false): string {
  if (!value) {
    return '—';
  }

  const [date, time = ''] = value.split('T');
  const [year, month, day] = date.split('-');

  return `${day}/${month}${withYear ? `/${year}` : ''} ${time.slice(
    0,
    5,
  )}`.trim();
}

export function formatDay(value: string): string {
  const [year, month, day] = value.split('-');

  return `${day}/${month}/${year}`;
}

export const WEEKDAYS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

export const MONTHS = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

export function isoDay(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}`;
}

// "2026-10" -> primeiro e último dia do mês
export function monthRange(month: string): { from: string; to: string } {
  const [year, index] = month.split('-').map(Number);

  return {
    from: isoDay(new Date(year, index - 1, 1)),
    to: isoDay(new Date(year, index, 0)),
  };
}

// "2026-10" + 1 -> "2026-11"
export function shiftMonth(month: string, delta: number): string {
  const [year, index] = month.split('-').map(Number);

  return isoDay(new Date(year, index - 1 + delta, 1)).slice(0, 7);
}

// "2026-10" -> "OUTUBRO/2026"
export function monthLabel(month: string): string {
  const [year, index] = month.split('-').map(Number);

  return `${MONTHS[index - 1].toUpperCase()}/${year}`;
}

// tamanho da foto reduzida mantendo a proporção (lado maior <= max)
export function fitSize(
  width: number,
  height: number,
  max = 1600,
): { width: number; height: number } {
  const scale = Math.min(1, max / Math.max(width, height));

  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
  };
}

// diferença dentro de 1% das saídas (ou 20 l) não chama atenção
export function differenceLevel(
  difference: number | null,
  erp: number | null,
): 'ok' | 'warning' | 'none' {
  if (difference === null || erp === null) {
    return 'none';
  }

  const tolerance = Math.max(20, Math.abs(erp) * 0.01);

  return Math.abs(difference) <= tolerance ? 'ok' : 'warning';
}

const round3 = (value: number) => Math.round(value * 1000) / 1000;

export interface SheetTotals {
  pumps: Record<string, { calibration: number; net: number }>;
  net: number;
  inLiters: number; // descargas anotadas no posto
  documents: number; // nº de nf anotadas no posto
  erpIn: number; // entradas lançadas no gescooper
  erpOut: number;
  difference: number;
}

// linha "TOTAL" da folha controle diesel
export function sheetTotals(
  days: FuelSummaryDay[],
  pumpIds: string[],
): SheetTotals {
  const pumps: SheetTotals['pumps'] = {};

  pumpIds.forEach(id => {
    const rows = days
      .map(day => day.pumps.find(item => item.pump_id === id))
      .filter(Boolean);

    pumps[id] = {
      calibration: round3(
        rows.reduce((sum, row) => sum + (row?.calibration_liters || 0), 0),
      ),
      net: round3(rows.reduce((sum, row) => sum + (row?.net || 0), 0)),
    };
  });

  const sum = (pick: (day: FuelSummaryDay) => number | null) =>
    round3(days.reduce((total, day) => total + (pick(day) || 0), 0));

  return {
    pumps,
    net: sum(day => day.net_liters),
    inLiters: sum(day => day.in_liters),
    documents: days.reduce((total, day) => total + day.in_documents.length, 0),
    erpIn: sum(day => day.erp_in),
    erpOut: sum(day => day.erp_out),
    difference: sum(day => day.difference),
  };
}

// número no padrão do excel em português (vírgula, sem milhar)
const csvNumber = (value: number | null | undefined) =>
  value === null || value === undefined ? '' : String(value).replace('.', ',');
const csvCell = (value: string) =>
  /[;"\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
const csvLine = (cells: string[]) => cells.map(csvCell).join(';');

// a folha "controle diesel" do mês, na mesma ordem de colunas da planilha
export function sheetCsv(
  summary: FuelSummary,
  pumpIds: string[],
  month: string,
): string {
  const pumps = pumpIds.map(
    id => summary.pumps.find(pump => pump.id === id)?.name || 'Bomba',
  );
  const totals = sheetTotals(summary.days, pumpIds);

  const header = [
    'DATA',
    ...pumps.flatMap(name => [
      `${name.toUpperCase()} - Leitura 7h00`,
      `${name.toUpperCase()} - Leitura 17h`,
      `${name.toUpperCase()} - Aferição Bomba (L)`,
      `${name.toUpperCase()} - Total Bombeado Dia (L)`,
    ]),
    'Entrada NF (L)',
    'Nº NF',
    'Entrada no GesCooper (L)',
    'Nº NF no GesCooper',
    'Saídas no Sistema (L)',
    'Diferença (L)',
    'Ocorrências',
  ];

  const rows = summary.days.map(day =>
    csvLine([
      formatDay(day.day),
      ...pumpIds.flatMap(id => {
        const row = day.pumps.find(item => item.pump_id === id);

        return [
          csvNumber(row?.opening),
          csvNumber(row?.closing),
          row?.calibration_liters ? csvNumber(row.calibration_liters) : '',
          csvNumber(row?.net),
        ];
      }),
      day.in_liters ? csvNumber(day.in_liters) : '',
      day.in_documents.join(', '),
      day.erp_in ? csvNumber(day.erp_in) : '',
      day.erp_in_documents.join(', '),
      csvNumber(day.erp_out),
      csvNumber(day.difference),
      day.occurrences.join(' | '),
    ]),
  );

  const total = csvLine([
    'TOTAL',
    ...pumpIds.flatMap(id => [
      '',
      '',
      csvNumber(totals.pumps[id].calibration),
      csvNumber(totals.pumps[id].net),
    ]),
    csvNumber(totals.inLiters),
    '',
    csvNumber(totals.erpIn),
    '',
    csvNumber(totals.erpOut),
    csvNumber(totals.difference),
    '',
  ]);

  return [
    csvLine([`CONTROLE DIESEL (8607) - MÊS: ${monthLabel(month)}`]),
    csvLine(header),
    ...rows,
    total,
  ].join('\r\n');
}

// histórico de leituras (csv com ; para abrir direto no excel)
export function readingsCsv(readings: FuelReading[], origin: string): string {
  const header = [
    'Data',
    'Hora',
    'Turno',
    'Bomba',
    'Encerrante',
    'Encerrante anterior',
    'Litros',
    'Lançado por',
    'Ocorrência',
    'Situação',
    'Foto',
  ];

  const rows = readings.map(item => {
    const [date, time = ''] = item.read_at.split('T');

    return csvLine([
      formatDay(date),
      time.slice(0, 5),
      SLOT_LABEL[item.slot] || item.slot,
      item.pump_name,
      csvNumber(item.reading),
      csvNumber(item.previous_reading),
      csvNumber(item.liters),
      item.access_name || '',
      item.notes || '',
      item.canceled_at
        ? `Anulada por ${item.canceled_by_name || '—'}: ${
            item.cancel_reason || ''
          }`
        : 'Válida',
      item.photo_url ? `${origin}${item.photo_url}` : '',
    ]);
  });

  return [header.join(';'), ...rows].join('\r\n');
}

// baixa um csv que abre no excel com acento (bom no começo do arquivo)
export function downloadCsv(csv: string, fileName: string): void {
  const blob = new Blob([`${String.fromCharCode(0xfeff)}${csv}`], {
    type: 'text/csv;charset=utf-8',
  });
  const link = document.createElement('a');

  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(link.href);
}
